import axios, { AxiosRequestHeaders, AxiosResponse } from "axios";
import authOperations from "configs/authService";
import { ApiError } from "object-models/shared/api-error.model";
import { ExceptionModel } from "object-models/shared/exception.model";
import NotificationHelper from "services/notification-helper";
import appStorage from "utilities/app-storage";
import { Utilities } from "utilities/utilities";

const apiClient = axios.create();

apiClient.interceptors.request.use(async (config) => {
  await authOperations.getRefreshToken();

  const accessToken = appStorage.getAccessToken();

  if (accessToken) {
    if (config.headers && typeof config.headers.set === "function") {
      config.headers.set("Authorization", `Bearer ${accessToken}`);
    } else {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
      } as AxiosRequestHeaders;
    }
  }

  return config;
});

class CommonApiHandler {
  baseURL = "/api/";

  private getProblemDetailsMessage(responseData: unknown): string | undefined {
    if (!responseData || typeof responseData !== "object") {
      return undefined;
    }

    const data = responseData as {
      errors?: unknown;
      detail?: unknown;
      title?: unknown;
      message?: unknown;
      Message?: unknown;
    };

    if (data.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
      const messages = Object.values(data.errors as Record<string, unknown>)
        .flatMap((value) => {
          if (Array.isArray(value)) {
            return value.filter((item): item is string => typeof item === "string");
          }

          if (typeof value === "string") {
            return [value];
          }

          return [] as string[];
        })
        .map((message) => message.trim())
        .filter(Boolean);

      if (messages.length > 0) {
        return messages.join(" ");
      }
    }

    if (typeof data.detail === "string" && data.detail.trim()) {
      return data.detail.trim();
    }

    if (typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }

    if (typeof data.Message === "string" && data.Message.trim()) {
      return data.Message.trim();
    }

    if (typeof data.title === "string" && data.title.trim()) {
      return data.title.trim();
    }

    return undefined;
  }

 get<T>(url: string, responseType: 'json' | 'blob' = 'json', signal?: AbortSignal): Promise<T> {
    url = this.getResourceURL(url);
    const header = this.getHeaders();
    return apiClient
      .get(url, {
        headers: header,
        responseType: responseType,
        signal: signal,
      })
      .then((response) => {
        return response.data;
      })
      .catch((ex) => Promise.reject(this.exceptionHandler(ex)));
  }

  getWithResponse<T>(
    url: string,
    responseType: "json" | "blob" = "json",
    signal?: AbortSignal
  ): Promise<AxiosResponse<T>> {
    url = this.getResourceURL(url);
    const header = this.getHeaders();
    return apiClient
      .get<T>(url, {
        headers: header,
        responseType: responseType,
        signal: signal,
      })
      .then((response) => response)
      .catch((ex) => Promise.reject(this.exceptionHandler(ex)));
  }

  delete<T>(url: string): Promise<T> {
    url = this.getResourceURL(url);
    const header = this.getHeaders();
    return apiClient
      .delete(url, {
        headers: header,
      })
      .then((response) => {
        return response.data;
      })
      .catch((ex) => Promise.reject(this.exceptionHandler(ex)));
  }

  post<TResponse, TRequest = unknown>(url: string, data: TRequest,headersType: 'json' | 'form' | 'blob' = 'json'): Promise<TResponse> {
    url = this.getResourceURL(url);
    const header =  headersType === 'form' ? this.getHeaders('multipart/form-data'): this.getHeaders();
    return apiClient
      .post(url, data, {
        headers: header,
        ...(headersType === 'blob' ? { responseType: 'blob' as const } : {}),
      })
      .then((response) => {
        return response.data;
      })
      .catch((ex) => Promise.reject(this.exceptionHandler(ex)));
  }

  put<TResponse, TRequest = unknown>(url: string, data: TRequest,headersType: 'json' | 'form' = 'json'): Promise<TResponse> {
    url = this.getResourceURL(url);
    const header =  headersType === 'form' ? this.getHeaders('multipart/form-data'): this.getHeaders();
    return apiClient
      .put(url, data, {
        headers: header,
      })
      .then((response) => {
        return response.data;
      })
      .catch((ex) => Promise.reject(this.exceptionHandler(ex)));
  }
  patch<TResponse, TRequest = unknown>(url: string, data: TRequest): Promise<TResponse> {
    url = this.getResourceURL(url);
    const header = this.getHeaders();
    return apiClient
      .patch(url, data, {
        headers: header,
      })
      .then((response) => {
        return response.data;
      })
      .catch((ex) => Promise.reject(this.exceptionHandler(ex)));
  }

  private getResourceURL(url: string): string {
    this.baseURL = process.env.REACT_APP_BASE_URL ?? this.baseURL;
    return this.baseURL + url;
  }

  private getHeaders(
    contentType: "application/json" | "multipart/form-data" = "application/json"
  ): AxiosRequestHeaders {
    return {
      "Content-Type": contentType,
    } as AxiosRequestHeaders;
  }

  private getErrorMessage(ex: any): string {
    const responseData = ex?.response?.data;

    if (typeof responseData === "string") {
      return responseData;
    }

    const problemDetailsMessage = this.getProblemDetailsMessage(responseData);
    if (problemDetailsMessage) {
      return problemDetailsMessage;
    }

    if (
      responseData &&
      typeof responseData === "object" &&
      typeof responseData.Message === "string"
    ) {
      return responseData.Message;
    }

    if (
      responseData &&
      typeof responseData === "object" &&
      typeof responseData.message === "string"
    ) {
      return responseData.message;
    }

    if (typeof ex?.message === "string") {
      return ex.message;
    }

    return "An unexpected error occurred.";
  }

  private exceptionHandler(ex: any): ApiError {
    if (ex?.response) {
      return this.handleException(ex.response as ExceptionModel, ex);
    }

    // Intentionally aborted requests (AbortController / CancelToken) should be silent.
    if (axios.isCancel(ex)) {
      return new ApiError(this.getErrorMessage(ex), undefined, undefined, ex);
    }

    // No response object means a network-level failure (offline, timeout, CORS).
    const message = this.getErrorMessage(ex);
    NotificationHelper.notifyError(message);
    return new ApiError(message, undefined, undefined, ex);
  }

  private handleException(
    response: ExceptionModel,
    originalException?: any
  ): ApiError {
    if (response.status === 401) {
      if (Utilities.isTokenExpired()) {
        Utilities.redirectToLogout();
        return new ApiError("Session expired.", 401, response.data, originalException);
      }

      const message =
        "Unauthorized. You don't have permission to access this functionality.";
      NotificationHelper.notifyError(message);
      return new ApiError(message, 401, response.data, originalException);
    }

    if (response.status === 400) {
      let message = "Invalid request data.";

      if (typeof response.data === "string") {
        message = response.data;
      } else {
        const problemDetailsMessage = this.getProblemDetailsMessage(response.data);
        if (problemDetailsMessage) {
          message = problemDetailsMessage;
        }
      }

      return new ApiError(message, 400, response.data, originalException);
    }

    if (response.data) {
      let message = "An unexpected error occurred.";

      if (typeof response.data === "string") {
        message = response.data;
      } else {
        const extractedMessage = this.getProblemDetailsMessage(response.data);
        if (extractedMessage) {
          message = extractedMessage;
        }
      }

      if (
        response.status !== 409 ||
        response.data.errorCode !== "COPY_CONFIRMATION_REQUIRED"
      ) {
        NotificationHelper.notifyError(message);
      }
      return new ApiError(message, response.status, response.data, originalException);
    }

    if (response.status === 500 || !response.data) {
      const message =
        "Something went wrong. Please retry, and if the error persists please contact the administrator.";
      NotificationHelper.notifyError(message);
      return new ApiError(message, response.status, response.data, originalException);
    }

    return new ApiError(
      this.getErrorMessage(originalException ?? response),
      response.status,
      response.data,
      originalException
    );
  }
}

const apiHandler = new CommonApiHandler();
export default apiHandler;